import { Button, Card, Col, DatePicker, message, Modal, Row, Select, Space, Spin, Table, Tag, Typography } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import type { TableProps } from "antd";
import { componentPalette } from "src/theme/component-palette";
import { BsTable } from "react-icons/bs";
import { PiChartBarFill } from "react-icons/pi";
import { flareCaseFilterDropdown } from "src/entity/flare/flare.constants";
import {
  EmissionKeys,
  EmissionObject,
  EmissionObjectEnum,
  MapEmissionObject,
  MonthsDisplayNoTotal,
  UnitAllEnum,
} from "src/entity/common/unit.constant";
import { TbRefresh } from "react-icons/tb";
import { mainReportStore } from "src/stores/main-report-store";
import { useShallow } from "zustand/react/shallow";
import PieDonutChart from "./charts/PieDonutChart";
import { sortYears } from "src/utils/array";
import {
  EnumReportScope,
  INVENTORY_CHARTS_KEYS,
  MAP_LABEL_TO_CHARTS,
  ResponseMainReportData,
  ResponseReportDataYearly,
  ResponseSummarizeMainReport,
  SymbolKeys,
  TmpMonthlyKey,
  TmpResponseMainReportData,
} from "src/entity/main-report/main-report.entity";
import { excelStore } from "src/stores/excel-store.interface";
import { Dayjs } from "dayjs";
import ColumnChartNormal from "./charts/ColumnChartNormal";
import StackedColumnChart from "./charts/StackedColumnChart";
import { uniqueId } from "lodash";
import { DEFAULT_MONTHLY_OBJECT_VALUE, DROPDOWN_COMPANY_LISTS } from "src/entity/common/common.constant";
import { formatNumber, parseAndFormatScientificNumber } from "src/utils/process-number";
import { ArrowDownOutlined, ArrowUpOutlined, CloseOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { DEFAULT_STACKED_COLUMN_CHART_REPORT } from "src/entity/main-report/main-report.constant";
import DownIcon from "../../../assets/additional/down.svg";
import UpIcon from "../../../assets/additional/up.svg";
import ApproveReport from "./ApproveReport";
import { vocApprovalStore } from "src/stores/voc-approval-store";
import { CommonSuccess } from "src/entity/common/menus-enum.enum";
import { profileStore } from "src/stores/profile-store";
import { RolesNameEnum } from "src/entity/common/roles.enum";

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;
interface ReportTableProps {
  selectedButton: string;
  setSelectedButton: (selected: string) => void;
  selectedYear: string[];
  setSelectedYear: (year: string[]) => void;
  selectedCompany: string;
  setSelectedCompany: (year: string) => void;
  selectedEmission: string;
  setSelectedEmission: (year: string) => void;
  selectedCase: string[];
  setSelectedCase: (flareCase: string[]) => void;
  scope: EnumReportScope;
  optionDropdownCompany: { label: string; value: string }[];
  optionDropdownYears: { label: string; value: string }[];
  selectedUnit: UnitAllEnum;
  setSelectedUnit: (unit: UnitAllEnum) => void;
  rangeYear: [Dayjs, Dayjs];
  setRangeYear: (ranges: [Dayjs, Dayjs]) => void;
}

const blackListArray = [SymbolKeys.ABSOLUTE, SymbolKeys.INTENSITY_TARGET, SymbolKeys.INTENSITY];
const ReportTable: React.FC<ReportTableProps> = ({
  selectedButton,
  setSelectedButton,
  selectedYear,
  setSelectedYear,
  selectedCompany,
  setSelectedCompany,
  selectedEmission,
  setSelectedEmission,
  selectedCase,
  setSelectedCase,
  scope,
  optionDropdownCompany,
  selectedUnit,
  setSelectedUnit,
  rangeYear,
  setRangeYear,
  optionDropdownYears,
}) => {
  const currentYear = new Date().getFullYear();
  const loading = mainReportStore(useShallow((state) => state.loading));
  const getMainReport = mainReportStore(useShallow((state) => state.getMainReport));
  const data = mainReportStore(useShallow((state) => state.data));
  const dataYearly = mainReportStore(useShallow((state) => state.dataYearly));
  const dataSummarizeReport = mainReportStore(useShallow((state) => state.dataSummarizeReport));
  const getMainReportOverviews = mainReportStore(useShallow((state) => state.getMainReportOverviews));

  const loadingChartMonth = mainReportStore(useShallow((state) => state.loadingChartMonth));
  const loadingChartYear = mainReportStore(useShallow((state) => state.loadingChartYear));
  const loadingSummarizeReport = mainReportStore(useShallow((state) => state.loadingSummarizeReport));

  const getMainReportCharts = mainReportStore(useShallow((state) => state.getMainReportCharts));
  const getSummarizeMainReport = mainReportStore(useShallow((state) => state.getSummarizeMainReport));

  const excelSetQueryStr = excelStore(useShallow((state) => state.setQueryStr));
  const excelQueryStr = excelStore(useShallow((state) => state.queryStr));
  // console.log("[TableView] excelQueryStr : ", excelQueryStr);

  const [isViewMore, setIsViewMore] = useState<boolean>(false);

  // Approve Modal
  const [isOpenViewApproveModal, setIsOpenViewApproveModal] = useState<boolean>(false);
  const [selectedSourceApproval, setSelectedSourceApproval] = useState<string[]>([]);
  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);
  const [comment, setComment] = useState<string>("");

  const [messageApi, contextHolder] = message.useMessage();

  // Approval Store
  const loadingApprovalOption = vocApprovalStore(useShallow((state) => state.loadingApprovalOption));
  const approvalOptionMonthly = vocApprovalStore(useShallow((state) => state.approvalOptionMonthly));
  const getAvailableApprovalMonthsStore = vocApprovalStore(useShallow((state) => state.getAvailableApprovalMonthsStore));
  const loadingApproveAction = vocApprovalStore(useShallow((state) => state.loadingApproveAction));
  const approveMultipleSourceStore = vocApprovalStore(useShallow((state) => state.approveMultipleSourceStore));

  // Permission
  const profile = profileStore(useShallow((state) => state.profile));
  const companyGroupPermission = vocApprovalStore(useShallow((state) => state.companyGroupPermission));
  const getCompanyGroupPermission = vocApprovalStore(useShallow((state) => state.getCompanyGroupPermission));

  const filterSummarizeCompanies = useMemo(() => {
    if (!dataSummarizeReport?.length) return [];

    const filtered = dataSummarizeReport.filter((base) => DROPDOWN_COMPANY_LISTS.includes(base.companyName.toUpperCase()));
    return filtered;
  }, [dataSummarizeReport]);

  const datasourceMonthlyForChart = useMemo(() => {
    const response: Record<string, any[]> = {
      stackColumnChart: [],
      columnChart: [],
      pieChart: [],
    };

    const excludeBlackList = data.filter((item) => !blackListArray.includes(item.symbol as SymbolKeys));

    response.stackColumnChart = excludeBlackList.map((item) => {
      // console.log("ITEM COLUMN : ", item);
      const { jan, feb, mar, apr, may, jun, jul, aug, sep, oct, nov, dec } = item.Ton.summary;
      //   console.log(`${item.label} : `, { jan, feb, mar, apr, may, jun, jul, aug, sep, oct, nov, dec });
      const processDataTon = [+jan, +feb, +mar, +apr, +may, +jun, +jul, +aug, +sep, +oct, +nov, +dec];

      return {
        name: MAP_LABEL_TO_CHARTS[item.label as INVENTORY_CHARTS_KEYS],
        data: processDataTon,
      };
    });

    const intensityData = data.find((item) => item.symbol === SymbolKeys.INTENSITY);
    const intensityTargetData = data.find((item) => item.symbol === SymbolKeys.INTENSITY_TARGET);
    const { jan, feb, mar, apr, may, jun, jul, aug, sep, oct, nov, dec } = intensityData?.Kg.summary ?? DEFAULT_MONTHLY_OBJECT_VALUE;
    const intensityTarget = intensityTargetData?.Ton.summary ?? DEFAULT_MONTHLY_OBJECT_VALUE;
    response.columnChart = [
      {
        name: "Intensity",
        type: "column",
        data: [jan, feb, mar, apr, may, jun, jul, aug, sep, oct, nov, dec],
      },
      {
        name: "Target",
        type: "line",
        data: [
          intensityTarget.jan,
          intensityTarget.feb,
          intensityTarget.mar,
          intensityTarget.apr,
          intensityTarget.may,
          intensityTarget.jun,
          intensityTarget.jul,
          intensityTarget.aug,
          intensityTarget.sep,
          intensityTarget.oct,
          intensityTarget.nov,
          intensityTarget.dec,
        ],
      },
    ];

    const findFUGITIVE = excludeBlackList.find((item) => item.symbol === SymbolKeys.FUGITIVE);
    const { total: totalFugitive } = findFUGITIVE?.Ton.summary ?? DEFAULT_MONTHLY_OBJECT_VALUE;
    const findCOMBUSTION = excludeBlackList.find((item) => item.symbol === SymbolKeys.COMBUSTION);
    const { total: totalCombustion } = findCOMBUSTION?.Ton.summary ?? DEFAULT_MONTHLY_OBJECT_VALUE;
    const findFLARE = excludeBlackList.find((item) => item.symbol === SymbolKeys.FLARE);
    const { total: totalFlare } = findFLARE?.Ton.summary ?? DEFAULT_MONTHLY_OBJECT_VALUE;
    const findTANK = excludeBlackList.find((item) => item.symbol === SymbolKeys.TANK);
    const { total: totalTank } = findTANK?.Ton.summary ?? DEFAULT_MONTHLY_OBJECT_VALUE;
    const findTERMINAL_MARKETING = excludeBlackList.find((item) => item.symbol === SymbolKeys.TERMINAL_MARKETING);
    const { total: totalMarketing } = findTERMINAL_MARKETING?.Ton.summary ?? DEFAULT_MONTHLY_OBJECT_VALUE;
    const findWASTE_WATER = excludeBlackList.find((item) => item.symbol === SymbolKeys.WASTE_WATER);
    const { total: totalWasteWater } = findWASTE_WATER?.Ton.summary ?? DEFAULT_MONTHLY_OBJECT_VALUE;
    const findOTHERS = excludeBlackList.find((item) => item.symbol === SymbolKeys.OTHERS);
    const { total: totalOthers } = findOTHERS?.Ton.summary ?? DEFAULT_MONTHLY_OBJECT_VALUE;
    response.pieChart = [totalFugitive, totalCombustion, totalFlare, totalTank, totalMarketing, totalWasteWater, totalOthers];
    // console.log("[Monthly] Response :: ", response);

    return response;
  }, [data]);

  const datasourceYearlyForChart = useMemo(() => {
    const response: Record<string, any[]> = {
      stackColumnChart: [],
      columnChart: [],
      pieChart: [],
    };

    const startYear = rangeYear[0].format("YYYY");
    const endYear = rangeYear[1].format("YYYY");
    const yearArray = Array.from({ length: parseInt(endYear) - parseInt(startYear) + 1 }, (_, index) =>
      (parseInt(startYear) + index).toString(),
    );
    const sortYear = sortYears(yearArray, "asc");
    // console.log("[Yearly] sortYear :: ", { sortYear, rangeYear });
    const excludeBlackList = dataYearly.length ? dataYearly.filter((item) => !blackListArray.includes(item.symbol as SymbolKeys)) : [];

    response.stackColumnChart = dataYearly.length
      ? excludeBlackList.map((item) => {
          return {
            name: MAP_LABEL_TO_CHARTS[item.label as INVENTORY_CHARTS_KEYS],
            data: sortYear.map((year) => item.data[year]?.Ton ?? 0),
          };
        })
      : DEFAULT_STACKED_COLUMN_CHART_REPORT;

    const intensityData = dataYearly.find((item) => item.symbol === SymbolKeys.INTENSITY);
    const intensityTargetData = dataYearly.find((item) => item.symbol === SymbolKeys.INTENSITY_TARGET);

    // console.log("[ChartView] datayearly : ", { dataYearly, sortYear });
    response.columnChart = [
      {
        name: "Intensity",
        type: "column",
        data: intensityData ? sortYear.map((year) => intensityData?.data[year]?.Kg) : [],
        // data: [4599, 5677],
      },
      {
        name: "Target",
        type: sortYear && sortYear.length === 1 ? "scatter" : "line",
        data: intensityTargetData ? sortYear.map((year) => intensityTargetData?.data[year]?.Ton) : [],
        // data: [9870, 12000],
      },
    ];

    const findFUGITIVE = excludeBlackList.find((item) => item.symbol === SymbolKeys.FUGITIVE);
    const totalFugitive = sortYear.reduce((prev, current) => {
      const value = findFUGITIVE?.data ? +findFUGITIVE?.data[current]?.Ton : 0;
      return prev + value;
    }, 0);
    const findCOMBUSTION = excludeBlackList.find((item) => item.symbol === SymbolKeys.COMBUSTION);
    const totalCombustion = sortYear.reduce((prev, current) => {
      const value = findCOMBUSTION?.data ? +findCOMBUSTION?.data[current]?.Ton : 0;
      return prev + value;
    }, 0);
    const findFLARE = excludeBlackList.find((item) => item.symbol === SymbolKeys.FLARE);
    const totalFlare = sortYear.reduce((prev, current) => {
      const value = findFLARE?.data ? +findFLARE?.data[current]?.Ton : 0;
      return prev + value;
    }, 0);
    const findTANK = excludeBlackList.find((item) => item.symbol === SymbolKeys.TANK);
    const totalTank = sortYear.reduce((prev, current) => {
      const value = findTANK?.data ? +findTANK?.data[current]?.Ton : 0;
      return prev + value;
    }, 0);
    const findTERMINAL_MARKETING = excludeBlackList.find((item) => item.symbol === SymbolKeys.TERMINAL_MARKETING);
    const totalMarketing = sortYear.reduce((prev, current) => {
      const value = findTERMINAL_MARKETING?.data ? +findTERMINAL_MARKETING?.data[current]?.Ton : 0;
      return prev + value;
    }, 0);
    const findWASTE_WATER = excludeBlackList.find((item) => item.symbol === SymbolKeys.WASTE_WATER);
    const totalWasteWater = sortYear.reduce((prev, current) => {
      const value = findWASTE_WATER?.data ? +findWASTE_WATER?.data[current]?.Ton : 0;
      return prev + value;
    }, 0);
    response.pieChart = [totalFugitive, totalCombustion, totalFlare, totalTank, totalMarketing, totalWasteWater];
    // console.log("[Yearly] Response :: ", response);

    return response;
  }, [dataYearly]);

  const isUserPermissionApprove = useMemo(() => {
    const permissions = [RolesNameEnum.APPROVER_1, RolesNameEnum.APPROVER_2, RolesNameEnum.SYSTEM_ADMIN].some((role) =>
      companyGroupPermission?.includes(role),
    );
    return permissions;
  }, [companyGroupPermission]);

  const monthColumns: TableProps<ResponseMainReportData>["columns"] = MonthsDisplayNoTotal.map((item) => {
    return {
      title: item.title,
      key: item.dataIndex,
      render: (_, record) => {
        const finalValue =
          record.symbol === SymbolKeys.INTENSITY_TARGET || record.symbol === SymbolKeys.INTENSITY
            ? record[record.symbol === SymbolKeys.INTENSITY_TARGET ? UnitAllEnum.TON : UnitAllEnum.KG]["summary"][
                item.dataIndex as keyof TmpMonthlyKey
              ]
            : record[selectedUnit as keyof TmpResponseMainReportData]["summary"][item.dataIndex as keyof TmpMonthlyKey];
        return <Text className="pr-3">{parseAndFormatScientificNumber(`${finalValue}`, 2)}</Text>;
      },
      align: "left",
    };
  });

  const handleChangeUnit = () => {
    if (selectedUnit === UnitAllEnum.KG) {
      setSelectedUnit(UnitAllEnum.TON);
    } else if (selectedUnit === UnitAllEnum.TON) {
      setSelectedUnit(UnitAllEnum.KG);
    }
  };

  const initialDataMonthlyForChart = async () => {
    const filters = {
      year: JSON.stringify(selectedYear),
      companyId: selectedCompany,
      emission: selectedEmission,
      flareCase: JSON.stringify(selectedCase),
      scope: EnumReportScope.MONTHLY,
    };

    getSummarizeMainReport({ year: JSON.stringify(selectedYear) });
    getMainReportCharts(filters);
    getMainReportOverviews(filters);
  };

  const initialDataYearlyForChart = async () => {
    const startYear = rangeYear[0].format("YYYY");
    const endYear = rangeYear[1].format("YYYY");
    const yearArray = Array.from({ length: parseInt(endYear) - parseInt(startYear) + 1 }, (_, index) =>
      (parseInt(startYear) + index).toString(),
    );
    const filters = {
      year: JSON.stringify(yearArray),
      companyId: selectedCompany,
      emission: selectedEmission,
      flareCase: JSON.stringify(selectedCase),
      scope: EnumReportScope.YEARLY,
    };

    await Promise.all([getMainReportCharts(filters), getMainReportOverviews(filters)]);
  };

  const initialData = async (scope: EnumReportScope, mode: string) => {
    if (mode === "table") {
      const startYear = rangeYear[0].format("YYYY");
      const endYear = rangeYear[1].format("YYYY");
      const yearArray = Array.from({ length: parseInt(endYear) - parseInt(startYear) + 1 }, (_, index) =>
        (parseInt(startYear) + index).toString(),
      );
      const pickYear = scope === EnumReportScope.YEARLY ? JSON.stringify(yearArray) : JSON.stringify(selectedYear);
      const filters = {
        year: pickYear,
        companyId: selectedCompany,
        emission: selectedEmission,
        flareCase: JSON.stringify(selectedCase),
        unit: selectedUnit,
        scope: scope,
      };
      // set query string for excel
      excelSetQueryStr(JSON.stringify(filters));

      await Promise.all([getMainReportOverviews(filters), getMainReport(filters)]);
    } else {
      // mode = chart
      if (scope === EnumReportScope.MONTHLY) initialDataMonthlyForChart();
      else if (scope === EnumReportScope.YEARLY) initialDataYearlyForChart();
    }
  };
  // ** Important: this useEffect will be defined before 'initialData();' **
  useEffect(() => {
    // console.log("[TableView] Initial UseEffect 2222!! ");

    let oldQuery = {};
    try {
      oldQuery = JSON.parse(excelQueryStr);
    } catch (error) {
      oldQuery = {};
    }
    const oldFilters = excelQueryStr
      ? {
          ...oldQuery,
          unit: selectedUnit,
        }
      : { unit: selectedUnit };

    excelSetQueryStr(JSON.stringify(oldFilters));
  }, [selectedUnit]);
  useEffect(() => {
    // console.log("[TableView] Initial UseEffect !! ");
    if (optionDropdownCompany.length) initialData(scope, selectedButton);
  }, [selectedYear, selectedCompany, selectedEmission, selectedCase, scope, selectedButton]);

  useEffect(() => {
    getAvailableMonthlyApproval();
  }, [selectedYear, selectedSourceApproval, selectedCompany]);

  // For get permission approve button
  useEffect(() => {
    getCompanyGroupPermission(`companyId=${selectedCompany}&&year=${selectedYear}`);
  }, [selectedCompany, selectedYear]);

  const getAvailableMonthlyApproval = () => {
    const params = new URLSearchParams();
    params.append("year", selectedYear.toString());
    params.append("companyId", selectedCompany);

    if (selectedSourceApproval.length) {
      selectedSourceApproval.forEach((item) => {
        params.append("menu[]", item === "Transport & Marketing" ? "Transportation" : item);
      });
    }

    const finalQuery = params.toString();
    getAvailableApprovalMonthsStore(finalQuery);
  };

  const columnsMonthly: TableProps<ResponseMainReportData>["columns"] = [
    {
      title: "No",
      key: "no",
      render: (_, __, index) => index + 1,
      align: "left",
    },
    {
      title: "Emission Source",
      key: "emissionSource",
      render: (_, record) => <Text>{record.label}</Text>,
      align: "left",
    },
    {
      title: "Emission",
      key: "emission",
      render: (_) => <Text>{MapEmissionObject[selectedEmission as EmissionKeys]}</Text>,
      align: "left",
    },
    {
      title: (
        <>
          <Space>
            Unit
            <Button onClick={handleChangeUnit} type="text" icon={<TbRefresh />} />
          </Space>
        </>
      ),
      key: "unit",
      render: (_, record) => (
        <Text>{record.symbol === SymbolKeys.INTENSITY_TARGET || record.symbol === SymbolKeys.INTENSITY ? "g/Ton" : selectedUnit}</Text>
      ),
      align: "left",
      width: 80,
    },
    ...monthColumns,
    {
      title: "Total",
      render: (_, record) => {
        const finalValue =
          record.symbol === SymbolKeys.INTENSITY_TARGET || record.symbol === SymbolKeys.INTENSITY
            ? record[record.symbol === SymbolKeys.INTENSITY ? UnitAllEnum.KG : UnitAllEnum.TON]["summary"].total
            : record[selectedUnit as keyof TmpResponseMainReportData]["summary"].total;
        return <Text>{parseAndFormatScientificNumber(`${finalValue}`, 2)}</Text>;
      },
      align: "left",
    },
  ];

  const yearColumns: TableProps<ResponseReportDataYearly>["columns"] = useMemo(() => {
    const startYear = rangeYear[0].format("YYYY");
    const endYear = rangeYear[1].format("YYYY");
    const yearArray = Array.from({ length: parseInt(endYear) - parseInt(startYear) + 1 }, (_, index) =>
      (parseInt(startYear) + index).toString(),
    );
    const pickYear = scope === EnumReportScope.YEARLY ? yearArray : selectedYear;

    const columnsYear: TableProps<ResponseReportDataYearly>["columns"] = pickYear.map((item) => {
      return {
        title: item,
        key: `${item}`,
        render: (_, record) => {
          const finalValue =
            record.symbol === SymbolKeys.INTENSITY_TARGET || record.symbol === SymbolKeys.INTENSITY
              ? record?.data[`${item}`]?.[record.symbol === SymbolKeys.INTENSITY_TARGET ? UnitAllEnum.TON : UnitAllEnum.KG]
              : record?.data[`${item}`]?.[selectedUnit as keyof TmpResponseMainReportData];
          return <Text className="pr-3">{parseAndFormatScientificNumber(`${finalValue}`, 2)}</Text>;
        },
        align: "left",
      };
    });

    return columnsYear;
  }, [selectedButton, selectedYear, rangeYear, selectedUnit]);

  const columnsYearly: TableProps<ResponseReportDataYearly>["columns"] = [
    {
      title: "No",
      key: "no",
      render: (_, __, index) => index + 1,
      align: "left",
    },
    {
      title: "Emission Source",
      key: "emissionSource",
      render: (_, record) => <Text>{record.label}</Text>,
      align: "left",
    },
    {
      title: "Emission",
      key: "emission",
      render: (_) => <Text>{MapEmissionObject[selectedEmission as EmissionKeys]}</Text>,
      align: "left",
    },
    {
      title: (
        <>
          <Space>
            Unit
            <Button onClick={handleChangeUnit} type="text" icon={<TbRefresh />} />
          </Space>
        </>
      ),
      key: "unit",
      render: (_, record) => (
        <Text>{record.symbol === SymbolKeys.INTENSITY_TARGET || record.symbol === SymbolKeys.INTENSITY ? "g/Ton" : selectedUnit}</Text>
      ),
      align: "left",
      width: 80,
    },
    ...yearColumns,
  ];

  const buttonStyle = (buttonName: string) => ({
    backgroundColor: selectedButton === buttonName ? componentPalette.primary : "initial",
    color: selectedButton === buttonName ? "white" : "initial",
  });

  const handleButtonMenuClick = (buttonName: string) => {
    setSelectedButton(buttonName);
    if (buttonName === "table") {
      setSelectedYear(selectedYear);
    } else if (buttonName === "chart") {
      //   setSelectedYear([currentYear.toString()]);
      // if (scope === EnumReportScope.MONTHLY) initialDataMonthlyForChart();
      // else if (scope === EnumReportScope.YEARLY) initialDataYearlyForChart();
    }
  };

  const handleChangeDropdown = (field: string, value: string[] | string) => {
    // console.log("Field & value : ", field, value);
    if (field === "year") setSelectedYear(Array.isArray(value) ? value : ([value] as string[]));
    else if (field === "company") setSelectedCompany(value as string);
    else if (field === "emission") setSelectedEmission(value as string);
    else if (field === "case") setSelectedCase(value as string[]);
  };

  const onChangeRangeYear = (rangeYear: [Dayjs, Dayjs]) => {
    const startYear = rangeYear[0].format("YYYY");
    const endYear = rangeYear[1].format("YYYY");
    const yearArray = Array.from({ length: parseInt(endYear) - parseInt(startYear) + 1 }, (_, index) =>
      (parseInt(startYear) + index).toString(),
    );
    setSelectedYear(yearArray);
  };

  const renderListRateUpOrDown = (item: ResponseSummarizeMainReport, index: number) => {
    const colorIcon = item.isGrowth ? "red" : "green";
    return (
      <Row className="w-full border-b-[1px] border-black-500" justify={"space-between"} key={index}>
        <Row className="w-full pt-4 pb-4" justify={"space-between"}>
          <Col span={6}>
            <Text>{item.companyName}</Text>
          </Col>
          <Col span={12} className="text-start">
            <Text>{formatNumber(item.value, 2)}</Text>
          </Col>
          <Col span={2}>
            <Text className="text-center">{item.unit}</Text>
          </Col>
          <Col span={4}>
            <Row>
              <div className="flex items-center">
                {item.isGrowth ? <img src={UpIcon} alt="upper" /> : <img src={DownIcon} alt="down" />}
              </div>{" "}
              <Text
                className="pl-1"
                style={{ color: colorIcon, fontWeight: "bold" }}
              >{`${item.growthRate ? formatNumber(+item.growthRate, 1) : 0} %`}</Text>
            </Row>
          </Col>
        </Row>
      </Row>
    );
  };

  const yearsRange = useMemo(() => {
    const startYear = rangeYear[0].format("YYYY");
    const endYear = rangeYear[1].format("YYYY");
    const yearArray = Array.from({ length: parseInt(endYear) - parseInt(startYear) + 1 }, (_, index) =>
      (parseInt(startYear) + index).toString(),
    );

    return yearArray;
  }, [rangeYear]);

  const renderChartZone = (type: EnumReportScope, loadingChart: boolean) => {
    const pickSourcedata = type === "year" ? datasourceYearlyForChart : datasourceMonthlyForChart;
    // console.log("pickSourcedata : ", pickSourcedata);
    if (!optionDropdownCompany.length) return <></>;
    return (
      <Row className="w-full">
        <Row className="w-full mt-4">
          <Col className="w-full">
            <Card>
              <Title level={4}>VOCs Emission (Ton)</Title>
              <StackedColumnChart loading={loadingChart} data={pickSourcedata?.stackColumnChart} scope={type} selectedYear={selectedYear} />
            </Card>
          </Col>
        </Row>

        <Row className="w-full mt-4">
          <Col className="w-full">
            <Card>
              <Title level={4}>VOCs Intensity (g/Ton)</Title>
              <ColumnChartNormal
                loading={loadingChart}
                data={pickSourcedata.columnChart}
                key={uniqueId("column-chart")}
                scope={type}
                selectedYear={type === "month" ? selectedYear : yearsRange}
              />
            </Card>
          </Col>
        </Row>

        {type !== "year" && (
          <Row className="w-full mt-4" gutter={[22, 0]}>
            <Col span={12}>
              <Card>
                <Title level={4}>Emission Source Ratio</Title>
                <PieDonutChart loading={loadingChart} data={pickSourcedata.pieChart} />
              </Card>
            </Col>

            <Col span={12}>
              <Card className="overflow-auto" style={{ maxHeight: "27.5rem" }}>
                <Title level={4}>Fully Emission by Company</Title>

                {loadingSummarizeReport ? (
                  <Spin spinning />
                ) : (
                  <>
                    {filterSummarizeCompanies && filterSummarizeCompanies.length > 3 && !isViewMore ? (
                      <>
                        {filterSummarizeCompanies.slice(0, 3).map((item, index) => {
                          return renderListRateUpOrDown(item, index);
                        })}
                        <div className="w-full cursor-pointer text-center mt-4" onClick={() => setIsViewMore(true)}>
                          <Text className="text-center">View More</Text>
                        </div>
                      </>
                    ) : (
                      filterSummarizeCompanies.map((item, index) => {
                        return renderListRateUpOrDown(item, index);
                      })
                    )}
                  </>
                )}
              </Card>
            </Col>
          </Row>
        )}
      </Row>
    );
  };

  const dataTableMonthly = useMemo(() => {
    if (selectedEmission !== EmissionObjectEnum.TVOC) {
      return data.filter((item) => item.symbol !== SymbolKeys.INTENSITY_TARGET);
    }
    return data;
  }, [data, selectedEmission]);

  const dataTableYearly = useMemo(() => {
    if (selectedEmission !== EmissionObjectEnum.TVOC) {
      return dataYearly.filter((item) => item.symbol !== SymbolKeys.INTENSITY_TARGET);
    }
    return dataYearly;
  }, [dataYearly, selectedEmission]);

  const onFinishActionReport = () => {
    getAvailableMonthlyApproval();
    setIsOpenViewApproveModal(false);
    setSelectedMonths([]);
    setSelectedSourceApproval([]);
    setComment("");
  };

  const actionReport = async (
    selectedSourceApproval: string[],
    selectedMonths: string[],
    comment: string,
    action: "approved" | "reject",
  ) => {
    const year = selectedYear.toString();
    if (!year) return;
    approveMultipleSourceStore({ selectedSourceApproval, selectedMonths, comment, year: Number(year), action, companyId: selectedCompany })
      .then(() => {
        if (action === "approved") {
          messageApi.open({
            type: "success",
            content: CommonSuccess.APPROVED_SUCCESSFULL,
          });
        } else if (action === "reject") {
          messageApi.open({
            type: "success",
            content: CommonSuccess.REJECT_SUCCESSFULL,
          });
        }
        onFinishActionReport();
        return;
      })
      .catch((error) => {
        const actionStr = action === "approved" ? "approve" : "reject";
        warningAlert(error?.response?.data?.message || error?.message, actionStr);
        return;
      });
  };

  const warningAlert = (message: string | string[], action: string) => {
    const warnModal = Modal.confirm({
      title: (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Can not {action}</span>
          <CloseOutlined onClick={() => warnModal.destroy()} style={{ cursor: "pointer" }} />
        </div>
      ),
      content: message,
      okText: "OK",
      icon: <InfoCircleOutlined style={{ color: componentPalette.redThird }} />,
      okButtonProps: { style: { textAlign: "center", backgroundColor: componentPalette.primary } },
      cancelButtonProps: { style: { display: "none" } },
      onOk: () => {},
      className: "custom-tmp-modal-warning",
    });
  };

  return (
    <div className="w-full pt-2" id={"wrap-report-table"}>
      <Row className="w-full mt-4">
        {contextHolder}
        <div>
          <Button
            size="middle"
            style={buttonStyle("table")}
            icon={<BsTable style={{ fontSize: "18px" }} />}
            onClick={() => handleButtonMenuClick("table")}
          />
          <Button
            size="middle"
            style={buttonStyle("chart")}
            icon={<PiChartBarFill style={{ fontSize: "18px" }} />}
            onClick={() => handleButtonMenuClick("chart")}
          />
        </div>
        {
          <>
            {scope === "year" ? (
              <RangePicker
                className="ml-4"
                picker="year"
                id={{
                  start: "startInput",
                  end: "endInput",
                }}
                allowClear={false}
                value={rangeYear}
                onChange={(e) => {
                  setRangeYear(e as [Dayjs, Dayjs]);
                  onChangeRangeYear(e as [Dayjs, Dayjs]);
                }}
              />
            ) : (
              <Select
                value={selectedYear}
                style={{ maxWidth: "20rem", marginLeft: 12 }}
                onChange={(value) => handleChangeDropdown("year", value)}
                options={optionDropdownYears}
                size="middle"
              />
            )}
            <Select
              value={selectedCompany}
              style={{ minWidth: "10rem", marginLeft: 12 }}
              onChange={(value) => handleChangeDropdown("company", value)}
              options={optionDropdownCompany}
              size="middle"
            />
            <Select
              value={selectedEmission}
              style={{ minWidth: 120, marginLeft: 12 }}
              onChange={(value) => handleChangeDropdown("emission", value)}
              options={EmissionObject}
              size="middle"
            />
            <Select
              value={selectedCase}
              style={{ minWidth: "10rem", marginLeft: 12 }}
              onChange={(value) => handleChangeDropdown("case", value)}
              options={flareCaseFilterDropdown}
              size="middle"
              mode={"multiple"}
            />
            {scope === EnumReportScope.MONTHLY && isUserPermissionApprove && (
              <Button
                type="primary"
                style={{
                  width: "140px",
                  marginLeft: 12,

                  display: isUserPermissionApprove ? "block" : "none",
                }}
                size="middle"
                onClick={() => {
                  setIsOpenViewApproveModal(true);
                }}
              >
                Approve
              </Button>
            )}
          </>
        }
      </Row>
      {selectedButton === "table" && (
        <>
          {scope === EnumReportScope.MONTHLY ? (
            <Table
              className="w-full mt-4"
              columns={columnsMonthly}
              dataSource={dataTableMonthly}
              pagination={false}
              loading={loading}
              rowKey={(item) => `${item.symbol}-${item.label}`}
              scroll={{ x: "max-content" }}
            />
          ) : (
            <Table
              className="w-full mt-4"
              columns={columnsYearly}
              dataSource={dataTableYearly}
              pagination={false}
              loading={loading}
              rowKey={(item) => `${item.symbol}-${item.label}`}
            />
          )}
        </>
      )}

      {selectedButton === "chart" && (
        <>
          {scope === EnumReportScope.MONTHLY
            ? renderChartZone(EnumReportScope.MONTHLY, loadingChartMonth)
            : renderChartZone(EnumReportScope.YEARLY, loadingChartYear)}
        </>
      )}

      <ApproveReport
        open={isOpenViewApproveModal}
        loadingApprovalOption={loadingApprovalOption}
        loadingApproveAction={loadingApproveAction}
        selectedYear={selectedYear.toString()}
        selectedMonths={selectedMonths}
        setSelectedMonths={setSelectedMonths}
        selectedSourceApproval={selectedSourceApproval}
        setSelectedSourceApproval={setSelectedSourceApproval}
        comment={comment}
        setComment={setComment}
        optionApprovalMonthly={approvalOptionMonthly ?? []}
        onClose={() => {
          onFinishActionReport();
        }}
        onApprove={(selectedSourceApproval, selectedMonths, comment, action) => {
          actionReport(selectedSourceApproval, selectedMonths, comment, action);
        }}
      />
    </div>
  );
};

export default ReportTable;
